import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Button,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  FaDownload,
  FaEye,
  FaExclamationTriangle,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "../clientTable/ClientTable.css";
import "../driverTable/PageOne/DarkTheme.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { driverAPI } from "../../api/apiSystem";
import PaginationComponent from "../PaginationComponent";
import { useSearchFilter } from "../useSearchFilter";
import ClientChatBox from "../chat/ClientChatBox";
import { formatDisplayDate } from "../../utils/helpers";

export default function RechnungenDriver() {
  const { t } = useTranslation();
  const driverVatLabel = "MWST.";
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const filteredData = useSearchFilter(data, searchTerm, ["order_id"]);

  // SweetAlert configuration
  const showSweetAlert = (icon, title, text, confirmButtonText = "OK") => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText,
      customClass: {
        confirmButton: "btn btn-primary",
      },
    });
  };

  const showErrorAlert = (text) => {
    return showSweetAlert("error", t("invoices.alerts.error"), text);
  };

  const showSuccessAlert = (text) => {
    return showSweetAlert("success", t("invoices.alerts.success"), text);
  };

  const showWarningAlert = (text) => {
    return showSweetAlert("warning", t("invoices.alerts.warning"), text);
  };

  // 🔹 Fetch invoices data from API
  const fetchInvoices = async (page = 1, perPage = 10) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: perPage,
      };

      console.log("📦 Fetching invoices with params:", params);

      const response = await driverAPI.transportsOrders.invoices(params);

      if (response && response.success) {
        console.log("✅ Invoices data:", response.data);

        const invoicesData = response.data.data || [];

        setData(invoicesData);

        // Set pagination
        setPagination(
          response.data.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: response.data.length,
          },
        );
      } else {
        console.error("❌ API Error:", response?.message);
        setError(response?.message || t("invoices.alerts.fetchError"));
        setData([]);
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(t("invoices.alerts.loadError"));
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // 🔹 Handle page change
  const handlePageChange = (page) => {
    fetchInvoices(page, pagination.per_page);
  };

  // 🔹 Handle per page change
  const handlePerPageChange = (e) => {
    const newPerPage = parseInt(e.target.value);
    fetchInvoices(1, newPerPage);
  };

  // 🔹 Handle download invoice
  const handleDownload = async (invoice) => {
    setDownloading(true);
    try {
      console.log("📥 Downloading invoice:", invoice.invoices);

      if (!invoice.invoices) {
        showWarningAlert(t("invoices.alerts.noInvoiceUrl"));
        return;
      }

      // Create temporary download link
      const link = document.createElement("a");
      link.href = invoice.invoices;
      link.target = "_blank";
      link.download = `invoice-${invoice.order_id || invoice.id}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("✅ Invoice download initiated");
      showSuccessAlert(t("invoices.alerts.downloadStarted"));
    } catch (err) {
      console.error("❌ Download error:", err);
      showErrorAlert(t("invoices.alerts.downloadError"));
    } finally {
      setDownloading(false);
    }
  };

  // 🔹 Handle view invoice
  const handleView = (invoice) => {
    console.log("👁️ Viewing invoice:", invoice.invoices);

    if (!invoice.invoices) {
      showWarningAlert(t("invoices.alerts.noInvoiceUrl"));
      return;
    }

    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  // 🔹 Clear Search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // 🔹 Get status badge
  const getStatusBadge = (status, invoiceStatus) => {
    const statusLower = status?.toLowerCase();
    let paymentBadge;

    if (statusLower === "bezahlt" || statusLower === "paid") {
      paymentBadge = (
        <span className="text-success fw-bold">
          {t("invoices.status.paid")} ✔
        </span>
      );
    } else {
      paymentBadge = (
        <span className="text-danger fw-bold">
          {t("invoices.status.unpaid")} ✖
        </span>
      );
    }

    if (invoiceStatus === 'canceled' || invoiceStatus === 'storniert') {
      return (
        <div className="d-flex flex-column align-items-center gap-1">
          {paymentBadge}
          <Badge bg="warning" text="dark" style={{ fontSize: '0.75rem' }}>
            {t("invoices.status.storniert") || "Storniert"}
          </Badge>
        </div>
      );
    }

    return paymentBadge;
  };

  // 🔹 Format currency
  const formatCurrency = (amount, currency = "€") => {
    if (!amount) return `0,00 ${currency}`;

    const cleanAmount = amount.toString().replace(",", ".");
    const number = parseFloat(cleanAmount);

    if (isNaN(number)) return `${amount} ${currency}`;

    return `${number.toFixed(2).replace(".", ",")} ${currency}`;
  };

  const toNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === "") return fallback;
    const normalized = String(value).replace(",", ".");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getCommissionAmount = (invoice) =>
    toNumber(invoice.commission_amount ?? invoice.sub_total, 0);

  const getCommissionVatRate = (invoice) =>
    toNumber(invoice.commission_vat_rate ?? invoice.vat_percentage ?? 19, 19);

  const getCommissionVatAmount = (invoice) => {
    const netAmount = getCommissionAmount(invoice);
    const vatRate = getCommissionVatRate(invoice);
    return (netAmount * vatRate) / 100;
  };

  const getCommissionTotal = (invoice) =>
    getCommissionAmount(invoice) + getCommissionVatAmount(invoice);

  if (loading) {
    return (
      <>
        <NavbarMenu />
        <div className="dark-table-container">
          <Container fluid>
            <div className="dark-loading">
              <Spinner
                animation="border"
                role="status"
                style={{ color: "#ff7b00" }}
              >
                <span className="visually-hidden">
                  {t("invoices.general.loading")}
                </span>
              </Spinner>
              <p className="mt-2" style={{ color: "#888" }}>
                {t("invoices.general.loadingInvoices")}
              </p>
            </div>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <NavbarMenu />
      <div>
        <Container fluid>
          <div className="p-4">
            <h4
              className="text-center fw-bold mb-4"
              style={{ color: "#ff7b00" }}
            >
              {t("invoices.title")}
            </h4>

            {error && (
              <Alert
                variant="danger"
                className="d-flex align-items-center"
                style={{
                  backgroundColor: "#2a2a2a",
                  borderColor: "#dc3545",
                  color: "#ff6b6b",
                }}
              >
                <FaExclamationTriangle className="me-2" />
                {error}
              </Alert>
            )}

            {/* 🔹 Search + Show Entries */}
            <Row className="mb-3 align-items-center">
              <Col
                xs={12}
                md={6}
                className="d-flex align-items-center mb-2 mb-md-0"
              >
                <span className="me-2 fw-bold" style={{ color: "#999" }}>
                  {t("invoices.general.show")}
                </span>
                <Form.Select
                  size="sm"
                  className="dark-search-input"
                  style={{ width: "80px" }}
                  value={pagination.per_page}
                  onChange={handlePerPageChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Form.Select>
                <span className="ms-2" style={{ color: "#999" }}>
                  {t("invoices.general.entries")}
                </span>
                <span className="ms-3" style={{ color: "#666" }}>
                  {t("invoices.general.total")}: {pagination.total}
                </span>
              </Col>
              {/* Search */}
              <Col xs={12} md={6} className="d-flex justify-content-md-end">
                <InputGroup
                  className="dark-input-group"
                  style={{ maxWidth: "350px" }}
                >
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    className="dark-search-input"
                    placeholder={t("invoices.search.placeholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      variant="outline-secondary"
                      onClick={clearSearch}
                      title={t("clientTable.clearSearch")}
                      style={{
                        backgroundColor: "#333",
                        border: "none",
                        color: "#888",
                      }}
                    >
                      ✕
                    </Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {/* 🔹 Table */}
            <div className="table-responsive">
              {filteredData.length === 0 ? (
                <div className="text-center py-4">
                  <p style={{ color: "#888" }}>
                    {data.length === 0
                      ? t("invoices.general.noInvoices")
                      : t("invoices.general.noMatchingInvoices")}
                  </p>
                </div>
              ) : (
                <>
                  <Table
                    bordered
                    hover
                    responsive
                    className="align-middle text-center orders-table"
                  >
                    <thead className="fw-bold bg-dark text-white">
                      <tr>
                        <th>{t("invoices.tableHeaders.id")}</th>
                        <th>{t("invoices.tableHeaders.orderNumber")}</th>
                        <th>{t("invoices.tableHeaders.date")}</th>
                        <th>{t("invoices.tableHeaders.netAmount")}</th>
                        <th>{driverVatLabel}</th>
                        <th>{t("invoices.tableHeaders.total")}</th>
                        <th>{t("invoices.tableHeaders.paymentStatus")}</th>
                        <th>{t("invoices.tableHeaders.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((invoice, idx) => (
                        <tr
                          key={invoice.id || idx}
                          // className={
                          //   idx % 2 === 0 ? "table-light" : "table-secondary"
                          // }
                        >
                          <td className="fw-bold">{invoice.id}</td>
                          <td>
                            {searchTerm ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: invoice.order_id?.replace(
                                    new RegExp(searchTerm, "gi"),
                                    (match) =>
                                      `<mark class="bg-warning">${match}</mark>`,
                                  ),
                                }}
                              />
                            ) : (
                              invoice.order_id
                            )}
                          </td>
                          <td>
                            {invoice.date || t("invoices.general.notAvailable")}
                          </td>
                          <td>{formatCurrency(getCommissionAmount(invoice))}</td>
                          <td>{formatCurrency(getCommissionVatAmount(invoice))}</td>
                          <td>
                            {formatCurrency(getCommissionTotal(invoice))}
                          </td>
                          <td>{getStatusBadge(invoice.status, invoice.invoice_status)}</td>
                          <td>
                            <div className="d-flex justify-content-center gap-2">
                              <Button
                                size="sm"
                                className="bg-orange"
                                style={{
                                  border: "none",
                                  borderRadius: "20px",
                                  padding: "5px 12px",
                                }}
                                onClick={() => handleDownload(invoice)}
                                disabled={downloading || !invoice.invoices}
                                title={t("invoices.buttons.downloadInvoice")}
                              >
                                {downloading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <FaDownload />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                className="bg-orange"
                                style={{
                                  border: "none",
                                  borderRadius: "20px",
                                  padding: "5px 12px",
                                }}
                                onClick={() => handleView(invoice)}
                                disabled={!invoice.invoices}
                                title={t("invoices.buttons.viewInvoice")}
                              >
                                <FaEye />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* 🔹 Pagination */}
                  {!loading && pagination.total > 0 && !searchTerm && (
                    <div>
                      <PaginationComponent
                        currentPage={pagination.current_page}
                        lastPage={pagination.last_page}
                        onPageChange={handlePageChange}
                        disabled={loading}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* 🔹 Invoice View Modal */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
        centered
        // className="dark-modal"
        className="position-absolute"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("invoices.modal.viewInvoice")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <div className="text-center">
              <h5 className="mb-3">
                {t("invoices.modal.invoice")}: {selectedInvoice.order_id}
              </h5>

              {/* PDF in iframe */}
              <div
                style={{
                  height: "500px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                }}
              >
                <iframe
                  src={selectedInvoice.invoices}
                  title={`Invoice-${selectedInvoice.order_id}`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </div>

              <div
                className="mt-3 p-3 rounded"
                style={{
                  backgroundColor: "var(--dark-bg-tertiary)",
                  border: "1px solid var(--dark-border)",
                }}
              >
                <Row>
                  <Col md={6}>
                    <p style={{ color: "var(--text-blue)" }}>
                      <strong>{t("invoices.tableHeaders.date")}:</strong>{" "}
                      {formatDisplayDate(selectedInvoice.date)}
                    </p>
                    <p style={{ color: "var(--text-blue)" }}>
                      <strong>{t("invoices.tableHeaders.netAmount")}:</strong>{" "}
                      {formatCurrency(getCommissionAmount(selectedInvoice))}
                    </p>
                    <p style={{ color: "var(--text-blue)" }}>
                      <strong>{driverVatLabel}:</strong>{" "}
                      {formatCurrency(getCommissionVatAmount(selectedInvoice))}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p style={{ color: "var(--text-blue)" }}>
                      <strong>
                        {t("invoices.tableHeaders.paymentStatus")}:
                      </strong>{" "}
                      {getStatusBadge(selectedInvoice.status)}
                    </p>
                    <p style={{ color: "var(--text-blue)" }}>
                      <strong>{t("invoices.tableHeaders.total")}:</strong>{" "}
                      {formatCurrency(getCommissionTotal(selectedInvoice))}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            style={{
              backgroundColor: "#333",
              border: "none",
              color: "#c5c5c5",
            }}
            onClick={() => setShowInvoiceModal(false)}
          >
            {t("invoices.buttons.close")}
          </Button>
          <Button
            style={{ backgroundColor: "#ff7b00", border: "none" }}
            onClick={() => selectedInvoice && handleDownload(selectedInvoice)}
            disabled={!selectedInvoice?.invoices}
          >
            <FaDownload className="me-2" />
            {t("invoices.buttons.download")}
          </Button>
        </Modal.Footer>
      </Modal>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
